import { translationAPI } from '@/utils/api'

export default {
  namespaced: true,
  
  state: {
    currentLanguage: localStorage.getItem('language') || 'en',
    translations: (() => {
      try {
        const stored = localStorage.getItem('translations')
        return stored ? JSON.parse(stored) : {}
      } catch (error) {
        console.error('Error parsing stored translations:', error)
        return {}
      }
    })(),
    lastUpdated: localStorage.getItem('translationsLastUpdated') || null,
    loading: false,
    error: null
  },
  
  mutations: {
    SET_CURRENT_LANGUAGE(state, language) {
      state.currentLanguage = language
      localStorage.setItem('language', language)
    },
    SET_TRANSLATIONS(state, translations) {
      state.translations = translations
      localStorage.setItem('translations', JSON.stringify(translations))
    },
    SET_LAST_UPDATED(state, timestamp) {
      state.lastUpdated = timestamp
      localStorage.setItem('translationsLastUpdated', timestamp)
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    }
  },
  
  actions: {
    async initialize({ dispatch, state }) {
      try {
        console.log('🚀 Initializing language store...')
        console.log('📱 Current platform: 1 (Vue.js web app)')
        
        // Check if we have valid cached translations first
        const now = new Date().getTime()
        const lastUpdated = state.lastUpdated ? new Date(state.lastUpdated).getTime() : 0
        const cacheAge = now - lastUpdated
        const cacheValid = cacheAge < 24 * 60 * 60 * 1000 // 24 hours for production
        
        // If we have valid cached translations, use them immediately
        if (Object.keys(state.translations).length > 0 && cacheValid) {
          console.log('✅ Using cached translations (age:', Math.round(cacheAge / 1000 / 60), 'minutes)')
          return { model: state.translations, fromCache: true }
        }
        
        // Only load fresh translations if cache is invalid or empty
        const result = await dispatch('loadTranslations', 1) // Platform 1 for Vue.js web app
        console.log('✅ Language store initialized successfully:', result)
        return result
      } catch (error) {
        console.error('❌ Failed to initialize language store:', error)
        // Return cached translations as fallback
        if (Object.keys(state.translations).length > 0) {
          console.log('🔄 Using cached translations as fallback')
          return { model: state.translations, fromCache: true, error: error.message }
        }
        throw error
      }
    },
    
    async loadTranslations({ commit, state }, platform = 1) {
      console.log('🔄 Starting loadTranslations for platform:', platform)
      console.log('📊 Current state:', {
        hasTranslations: Object.keys(state.translations).length > 0,
        lastUpdated: state.lastUpdated,
        currentLanguage: state.currentLanguage
      })
      
      commit('SET_LOADING', true)
      
      try {
        // Check if we have cached translations and they're recent (less than 24 hours old)
        const now = new Date().getTime()
        const lastUpdated = state.lastUpdated ? new Date(state.lastUpdated).getTime() : 0
        const cacheAge = now - lastUpdated
        const cacheValid = cacheAge < 24 * 60 * 60 * 1000 // 24 hours for production
        
        // Only log cache status in development
        if (process.env.NODE_ENV === 'development') {
          console.log('⏰ Cache check:', {
            now: new Date(now).toISOString(),
            lastUpdated: state.lastUpdated,
            cacheAgeMinutes: Math.round(cacheAge / 1000 / 60),
            cacheValid
          })
        }
        
        // If we have valid cached translations, use them
        if (Object.keys(state.translations).length > 0 && cacheValid) {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Using cached translations (age:', Math.round(cacheAge / 1000 / 60), 'minutes)')
          }
          commit('SET_LOADING', false)
          return { model: state.translations, fromCache: true }
        }
        
        // If no cached translations or cache is old, always load fresh
        console.log('🌐 Loading fresh translations from API for platform:', platform)
        const response = await translationAPI.mobileGetTranslationsByPlatform(platform)
        console.log('📡 Translation API response:', response)
        
        if (response.model && response.model.length > 0) {
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.log('📝 Processing', response.model.length, 'translations...')
          }
          
          // Convert translations array to key-value object with all 6 languages
          const translationsMap = {}
          let processedCount = 0
          
          response.model.forEach((item) => {
            const key = (item.Key || item.key || '').trim()
            if (key) {
              translationsMap[key] = {
                en: item.TextEng || item.textEng || item.texteng || key,
                lo: item.TextLao || item.textLao || item.textlao || key,
                th: item.TextThai || item.textThai || item.textthai || key,
                vn: item.TextVietnam || item.textVietnam || item.textvietnam || key,
                kh: item.TextCambo || item.textCambo || item.textcambo || key,
                zh: item.TextChiness || item.textChiness || item.textchiness || key
              }
              processedCount++
            }
          })
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Processed', processedCount, 'translations')
          }
          
          commit('SET_TRANSLATIONS', translationsMap)
          commit('SET_LAST_UPDATED', new Date().toISOString())
        } else {
          console.log('⚠️ No translations found from API')
          // Keep existing cached translations if API returns empty
          if (Object.keys(state.translations).length === 0) {
            console.log('🗑️ No cached translations, setting empty object')
            commit('SET_TRANSLATIONS', {})
          }
        }
        return response
      } catch (error) {
        console.error('❌ Error loading translations:', error)
        commit('SET_ERROR', error.message)
        
        // If API fails but we have cached translations, use them
        if (Object.keys(state.translations).length > 0) {
          console.log('🔄 API failed, using cached translations as fallback')
          return { model: state.translations, fromCache: true, error: error.message }
        }
        
        console.log('🗑️ No cached translations available, setting empty object')
        commit('SET_TRANSLATIONS', {})
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async refreshTranslations({ commit, state }, platform = 1) {
      commit('SET_LOADING', true)
      
      try {
        console.log('Force refreshing translations from API for platform:', platform)
        const response = await translationAPI.mobileGetTranslationsByPlatform(platform)
        console.log('Translation API response:', response)
        
        if (response.model && response.model.length > 0) {
          // Debug: Log the first item to see available properties
          if (response.model.length > 0) {
            console.log('🔍 Sample translation item properties:', Object.keys(response.model[0]))
            console.log('🔍 Sample translation item:', response.model[0])
          }
          // Convert translations array to key-value object with all 6 languages
          const translationsMap = {}
          response.model.forEach(item => {
            // Handle both uppercase and lowercase property names
            const key = (item.Key || item.key || '').trim()
            if (key) {
              translationsMap[key] = {
                en: item.TextEng || item.textEng || item.texteng || key,
                lo: item.TextLao || item.textLao || item.textlao || key,
                th: item.TextThai || item.textThai || item.textthai || key,
                vn: item.TextVietnam || item.textVietnam || item.textvietnam || key,
                kh: item.TextCambo || item.textCambo || item.textcambo || key,  // Cambodian/Khmer
                zh: item.TextChiness || item.textChiness || item.textchiness || key // Chinese
              }
            }
          })
          
          console.log('Updated translations:', translationsMap)
          commit('SET_TRANSLATIONS', translationsMap)
          commit('SET_LAST_UPDATED', new Date().toISOString())
        } else {
          console.log('No translations found from API')
          commit('SET_TRANSLATIONS', {})
        }
        return response
      } catch (error) {
        console.error('Error refreshing translations:', error)
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    setLanguage({ commit }, language) {
      commit('SET_CURRENT_LANGUAGE', language)
    },
    
    clearCache({ commit }) {
      commit('SET_TRANSLATIONS', {})
      commit('SET_LAST_UPDATED', null)
      localStorage.removeItem('translations')
      localStorage.removeItem('translationsLastUpdated')
    },
    
    clearTranslationCache({ commit }) {
      console.log('🗑️ Clearing translation cache...')
      commit('SET_TRANSLATIONS', {})
      commit('SET_LAST_UPDATED', null)
      localStorage.removeItem('translations')
      localStorage.removeItem('translationsLastUpdated')
      console.log('✅ Translation cache cleared successfully')
    },
    
    clearError({ commit }) {
      commit('SET_ERROR', null)
    }
  },
  
  getters: {
    currentLanguage: state => state.currentLanguage,
    translations: state => state.translations,
    loading: state => state.loading,
    error: state => state.error,
    lastUpdated: state => state.lastUpdated,
    
    // Get translated text for a key (optimized)
    t: (state) => (key) => {
      // Handle edge cases
      if (!key || typeof key !== 'string') {
        console.warn('Invalid translation key:', key)
        return key || ''
      }
      
      // Trim the key to remove any leading/trailing spaces
      const trimmedKey = key.trim()
      
      // Fast path: no key or no translations
      if (!trimmedKey || !state.translations[trimmedKey]) {
        // if (process.env.NODE_ENV === 'development') {
        //   console.warn(`🔤 Translation not found for key: "${trimmedKey}"`)
        // }
        return trimmedKey
      }
      
      try {
        const translation = state.translations[trimmedKey]
        const result = translation[state.currentLanguage] || translation.en || trimmedKey
        
        // // Only log in development mode
        // if (process.env.NODE_ENV === 'development') {
        //   console.log(`🔤 [${state.currentLanguage}] "${trimmedKey}" → "${result}"`)
        // }
        
        return result
      } catch (error) {
        console.error('❌ Error in translation getter for key:', trimmedKey, error)
        return trimmedKey
      }
    },
    
    // Memoized translation getter for better performance
    tMemoized: (state) => {
      const cache = new Map()
      const currentLang = state.currentLanguage
      
      return (key) => {
        // Check cache first
        const cacheKey = `${currentLang}:${key}`
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey)
        }
        
        // Fast path: no key or no translations
        if (!key || !state.translations[key]) {
          cache.set(cacheKey, key)
          return key
        }
        
        const translation = state.translations[key]
        const result = translation[currentLang] || translation.en || key
        
        // Cache the result
        cache.set(cacheKey, result)
        return result
      }
    },
    
    // Get all available languages (6 languages total)
    availableLanguages: () => [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'lo', name: 'Lao', nativeName: 'ລາວ' },
      { code: 'th', name: 'Thai', nativeName: 'ไทย' },
      { code: 'vn', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
      { code: 'kh', name: 'Khmer', nativeName: 'ខ្មែរ' },
      { code: 'zh', name: 'Chinese', nativeName: '中文' }
    ],
    
    // Check if translations are cached
    hasCachedTranslations: state => Object.keys(state.translations).length > 0,
    
    // Get cache age in minutes
    cacheAgeMinutes: state => {
      if (!state.lastUpdated) return null
      const now = new Date().getTime()
      const lastUpdated = new Date(state.lastUpdated).getTime()
      return Math.round((now - lastUpdated) / 1000 / 60)
    }
  }
} 